import { useEffect, useRef } from 'react';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { timeSince } from '@ais-ui/utils';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import Overlay from 'ol/Overlay';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { Point, LineString } from 'ol/geom';
import { Style, Stroke, Fill } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import type { MapBrowserEvent } from 'ol';
import type { WaypointAttributes } from '@ais-ui/synthetic-geo';
import type { Coordinate } from 'ol/coordinate';

/** Captured details of a hovered prediction line (SOG vector / predictor). */
interface PredictionHover {
  kind: 'sog' | 'predictor';
  label: string | null;
  durationSec: number;
  cog: number;
  sog: number;
  rot: number;
  rotDerived: boolean;
  coords: Coordinate[];
}

/** Squared distance between two map coordinates. */
function distSq(a: Coordinate, b: Coordinate): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
}

/**
 * For a 'track'-mode feature (single LineString with all coords), find the
 * waypoint closest to the given map coordinate.
 */
function findClosestWaypoint(
  coord: Coordinate,
  trackCoords: Coordinate[],
  waypoints: WaypointAttributes[],
): { index: number; wp: WaypointAttributes; point: Coordinate } | null {
  if (!trackCoords || !waypoints || trackCoords.length === 0) return null;

  let minDist = Infinity;
  let bestIdx = 0;
  for (let i = 0; i < trackCoords.length; i++) {
    const d = distSq(coord, trackCoords[i]);
    if (d < minDist) {
      minDist = d;
      bestIdx = i;
    }
  }

  const wp = waypoints[bestIdx];
  if (!wp) return null;
  return { index: bestIdx, wp, point: trackCoords[bestIdx] };
}

/**
 * Compute bearing (COG) in degrees from point A to point B (map coordinates).
 * Returns 0-360 degrees clockwise from north.
 */
function computeBearing(a: Coordinate, b: Coordinate): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const angleRad = Math.atan2(dx, dy);
  return ((angleRad * 180) / Math.PI + 360) % 360;
}

// --- Highlight styles ---

function createPointStyle(color: string): Style {
  return new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color }),
      stroke: new Stroke({ color: '#fff', width: 2 }),
    }),
  });
}

const HIGHLIGHT_SEGMENT_STYLE = new Style({
  stroke: new Stroke({ color: 'rgba(255, 255, 255, 0.7)', width: 6 }),
});

const HIGHLIGHT_PREDICTION_STYLE = new Style({
  stroke: new Stroke({ color: 'rgba(17, 24, 39, 0.9)', width: 3 }),
});

/** Format a duration in seconds as a short human label (e.g. "30 min"). */
function formatDuration(totalSec: number): string {
  if (totalSec >= 60) {
    const min = Math.round(totalSec / 60);
    return `${min} min`;
  }
  return `${totalSec} s`;
}

/** Describe a signed rate of turn, e.g. "6.0°/min stbd". */
function formatRot(rotDegMin: number): string {
  const mag = Math.abs(rotDegMin);
  if (mag < 0.1) return 'steady (0°/min)';
  const side = rotDegMin > 0 ? 'stbd' : 'port';
  return `${mag.toFixed(1)}°/min ${side}`;
}

/** Build the hover tooltip DOM for a prediction line. */
function renderPredictionTooltip(el: HTMLDivElement, p: PredictionHover): void {
  const isSog = p.kind === 'sog';
  const distanceNm = (p.sog * p.durationSec) / 3600;

  el.innerHTML = '';

  const header = document.createElement('div');
  header.className = 'px-2.5 py-1.5 text-sm font-semibold';
  header.textContent = `${p.label ? `${p.label} — ` : ''}${
    isSog ? 'SOG vector' : 'Predictor'
  }`;
  el.appendChild(header);

  const desc = document.createElement('div');
  desc.className = 'px-2.5 text-xs text-muted-foreground';
  desc.textContent = isSog
    ? 'Where the vessel reaches if it holds course and speed.'
    : 'Projected path accounting for the current turn.';
  el.appendChild(desc);

  const rows: string[] = [
    `COG ${Math.round(p.cog)}°`,
    `SOG ${p.sog.toFixed(1)} kn`,
  ];
  if (!isSog) {
    rows.push(`Turn ${formatRot(p.rot)}${p.rotDerived ? ' (derived)' : ''}`);
  }
  rows.push(
    `Over ${formatDuration(p.durationSec)} ≈ ${distanceNm.toFixed(1)} NM`,
  );

  const list = document.createElement('div');
  list.className = 'px-2.5 py-1.5 text-xs flex flex-col gap-0.5';
  for (const text of rows) {
    const row = document.createElement('div');
    row.textContent = text;
    list.appendChild(row);
  }
  el.appendChild(list);
}

/**
 * Adds a rich map hover tooltip for vector track features.
 * Shows vessel name, SOG, COG, and formatted datetime.
 * Highlights the hovered point (circle) and widens the line segment.
 */
export function useTrackHoverTooltip() {
  const { map } = useOpenLayersContext();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const overlayRef = useRef<Overlay | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const highlightSourceRef = useRef<VectorSource<Feature> | null>(null);

  // Keep formatter in a ref so the pointer-move handler always uses latest
  const formatRef = useRef(formatDateTimeWithTimeZone);
  formatRef.current = formatDateTimeWithTimeZone;

  useEffect(() => {
    if (!map) return;

    // Create tooltip DOM — matches the vessel hover Card style
    const el = document.createElement('div');
    el.className =
      'pointer-events-none rounded-md bg-popover text-popover-foreground shadow-sm border border-l-2 border-l-primary min-w-[128px]';
    el.style.display = 'none';
    document.body.appendChild(el);
    elementRef.current = el;

    const overlay = new Overlay({
      element: el,
      offset: [16, -16],
      positioning: 'bottom-left',
      stopEvent: false,
    });
    map.addOverlay(overlay);
    overlayRef.current = overlay;

    // Create highlight layer (sits above tracks)
    const highlightSource = new VectorSource<Feature>();
    const highlightLayer = new VectorLayer({
      source: highlightSource,
      zIndex: 9999,
    });
    map.addLayer(highlightLayer);
    highlightSourceRef.current = highlightSource;

    const handlePointerMove = (evt: MapBrowserEvent<PointerEvent>) => {
      if (evt.dragging) {
        el.style.display = 'none';
        highlightSource.clear();
        return;
      }

      let label: string | null = null;
      let timestamp: string | null = null;
      let sog: number | null = null;
      let cog: number | null = null;
      let segmentColor: string | null = null;
      let highlightPoint: Coordinate | null = null;
      let segmentStart: Coordinate | null = null;
      let segmentEnd: Coordinate | null = null;
      let prediction: PredictionHover | null = null;

      map.forEachFeatureAtPixel(
        evt.pixel,
        (featureLike) => {
          if (!('getStyle' in featureLike)) return false;
          const feature = featureLike as Feature;

          // Prediction line (SOG vector / predictor) — explain what it is.
          if (feature.get('_prediction')) {
            const geom = feature.getGeometry();
            const coords =
              geom && geom.getType() === 'LineString'
                ? (geom as LineString).getCoordinates()
                : [];
            prediction = {
              kind:
                feature.get('predictionKind') === 'sog' ? 'sog' : 'predictor',
              label: (feature.get('predictionLabel') as string) ?? null,
              durationSec:
                (feature.get('predictionDurationSec') as number) ?? 0,
              cog: (feature.get('predictionCog') as number) ?? 0,
              sog: (feature.get('predictionSog') as number) ?? 0,
              rot: (feature.get('predictionRot') as number) ?? 0,
              rotDerived: feature.get('predictionRotDerived') === true,
              coords,
            };
            return true;
          }

          const trackLabel = feature.get('trackLabel');
          if (!trackLabel) return false;

          label = trackLabel;

          // Per-segment feature (SOG/COG color modes)
          const directTimestamp = feature.get('trackTimestamp');
          if (directTimestamp) {
            timestamp = directTimestamp;
            sog = feature.get('trackSog') ?? null;
            cog = feature.get('trackCog') ?? null;
            highlightPoint = feature.get('trackStartCoord') ?? null;

            // Read segment color from feature style
            const featureStyle = feature.getStyle() as Style | null;
            if (featureStyle?.getStroke?.()) {
              segmentColor = featureStyle.getStroke()!.getColor() as string;
            }

            // Get segment geometry for highlight
            const geom = feature.getGeometry();
            if (geom && geom.getType() === 'LineString') {
              const lineCoords = (geom as LineString).getCoordinates();
              if (lineCoords.length >= 2) {
                segmentStart = lineCoords[0];
                segmentEnd = lineCoords[1];
                if (cog == null) {
                  cog = Math.round(
                    computeBearing(lineCoords[0], lineCoords[1]),
                  );
                }
              }
            }
            return true;
          }

          // Single-line feature ('track' mode) — find closest waypoint
          const waypoints = feature.get('trackWaypoints') as
            | WaypointAttributes[]
            | null;
          const trackCoords = feature.get('trackCoords') as Coordinate[] | null;
          if (waypoints && trackCoords) {
            // Read line color from feature style
            const featureStyle = feature.getStyle() as Style | null;
            if (featureStyle?.getStroke?.()) {
              segmentColor = featureStyle.getStroke()!.getColor() as string;
            }

            const closest = findClosestWaypoint(
              evt.coordinate,
              trackCoords,
              waypoints,
            );
            if (closest) {
              timestamp = closest.wp.timestamp;
              sog = closest.wp.sog_kn;
              cog = closest.wp.heading_deg ?? null;
              highlightPoint = closest.point;

              if (closest.index < trackCoords.length - 1) {
                segmentStart = trackCoords[closest.index];
                segmentEnd = trackCoords[closest.index + 1];
                if (cog == null) {
                  cog = Math.round(
                    computeBearing(
                      trackCoords[closest.index],
                      trackCoords[closest.index + 1],
                    ),
                  );
                }
              }
            }
          }

          return true;
        },
        { hitTolerance: 6 },
      );

      // Prediction lines take precedence — show their explanatory tooltip.
      if (prediction) {
        const p = prediction as PredictionHover;
        renderPredictionTooltip(el, p);
        el.style.display = '';
        overlay.setPosition(evt.coordinate);

        highlightSource.clear();
        if (p.coords.length >= 2) {
          const lineFeature = new Feature({
            geometry: new LineString(p.coords),
          });
          lineFeature.setStyle(HIGHLIGHT_PREDICTION_STYLE);
          highlightSource.addFeature(lineFeature);
        }
        return;
      }

      if (label) {
        // Build card-style tooltip content (matching vessel hover)
        const header = label as string;
        const infoParts: string[] = [];
        if (sog !== null) infoParts.push(`${(sog as number).toFixed(1)} kn`);
        if (cog !== null) infoParts.push(`${Math.round(cog as number)}°`);
        if (timestamp) {
          infoParts.push(formatRef.current(new Date(timestamp), true));
        }

        el.innerHTML = '';
        // Header
        const headerEl = document.createElement('div');
        headerEl.className = 'px-2.5 py-1.5 text-sm font-semibold';
        headerEl.textContent = header;
        el.appendChild(headerEl);

        // Info row
        if (infoParts.length > 0) {
          const infoEl = document.createElement('div');
          infoEl.className =
            'px-2.5 pb-1.5 text-xs text-muted-foreground flex items-center gap-2';

          // SOG with color dot
          if (sog !== null) {
            const sogSpan = document.createElement('span');
            sogSpan.className = 'flex items-center gap-1';
            const dot = document.createElement('span');
            dot.className = 'inline-block size-2 rounded-full';
            dot.style.backgroundColor = segmentColor ?? '#1e88e5';
            sogSpan.appendChild(dot);
            sogSpan.appendChild(
              document.createTextNode(`${(sog as number).toFixed(1)} kn`),
            );
            infoEl.appendChild(sogSpan);
          }
          if (cog !== null) {
            const cogSpan = document.createElement('span');
            cogSpan.textContent = `${Math.round(cog as number)}°`;
            infoEl.appendChild(cogSpan);
          }
          if (timestamp) {
            const timeSpan = document.createElement('span');
            timeSpan.className = 'opacity-70';
            timeSpan.textContent = formatRef.current(new Date(timestamp), true);
            infoEl.appendChild(timeSpan);

            const age = timeSince(new Date(timestamp));
            if (age) {
              const ageSpan = document.createElement('span');
              ageSpan.className = 'opacity-70';
              ageSpan.textContent = `(${age})`;
              infoEl.appendChild(ageSpan);
            }
          }
          el.appendChild(infoEl);
        }

        el.style.display = '';
        overlay.setPosition(evt.coordinate);

        // Apply highlight
        highlightSource.clear();
        const pointColor = segmentColor ?? '#1e88e5';
        if (highlightPoint) {
          const pointFeature = new Feature({
            geometry: new Point(highlightPoint),
          });
          pointFeature.setStyle(createPointStyle(pointColor));
          highlightSource.addFeature(pointFeature);
        }
        if (segmentStart && segmentEnd) {
          const lineFeature = new Feature({
            geometry: new LineString([segmentStart, segmentEnd]),
          });
          lineFeature.setStyle(HIGHLIGHT_SEGMENT_STYLE);
          highlightSource.addFeature(lineFeature);
        }
      } else {
        el.style.display = 'none';
        highlightSource.clear();
      }
    };

    map.on('pointermove', handlePointerMove);

    return () => {
      map.un('pointermove', handlePointerMove);
      map.removeOverlay(overlay);
      map.removeLayer(highlightLayer);
      el.remove();
    };
  }, [map]);
}
